import { useEffect, useMemo, useState, Fragment } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import BackLink from "@/components/BackLink";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Printer, FileDown, Layers } from "lucide-react";
import { inr, formatDate, amountInWords } from "@/lib/utils2";
import EmailDialog from "@/components/EmailDialog";

function firstOfMonth() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function PartyStatement() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [from, setFrom] = usePersistedState(`stmt:${id}:from`, () => sp.get("from") || firstOfMonth());
  const [to, setTo] = usePersistedState(`stmt:${id}:to`, () => sp.get("to") || todayISO());
  const [party, setParty] = useState(null);
  const [detail, setDetail] = useState({ bills: [], on_account: 0 });
  const [payments, setPayments] = useState([]);
  const [company, setCompany] = useState({});
  const [expandedBill, setExpandedBill] = useState(null);

  useEffect(() => { (async () => {
    const [p, bd, pm, s] = await Promise.all([
      api.get("/parties"),
      api.get(`/party/${id}/bills-detail`),
      api.get("/payments-received", { params: { party_id: id } }),
      api.get("/settings"),
    ]);
    setParty(p.data.find((x) => x.id === id));
    setDetail(bd.data);
    setPayments(pm.data);
    setCompany(s.data || {});
  })(); }, [id]);

  const inPeriod = (d) => (!from || d >= from) && (!to || d <= to);
  const billsInPeriod = useMemo(() => detail.bills.filter((b) => inPeriod(b.bill_date)), [detail.bills, from, to]);
  const paymentsInPeriod = useMemo(() => payments.filter((p) => inPeriod(p.date)), [payments, from, to]);

  const opening = useMemo(() => {
    const beforeBills = detail.bills.filter((b) => b.bill_date < from);
    const beforePays = payments.filter((p) => p.date < from);
    const billed = beforeBills.reduce((s, b) => s + b.total_amount, 0);
    const received = beforePays.reduce((s, p) => s + p.amount, 0);
    return billed - received;
  }, [detail.bills, payments, from]);

  const periodBilled = billsInPeriod.reduce((s, b) => s + b.total_amount, 0);
  const periodReceived = paymentsInPeriod.reduce((s, p) => s + p.amount, 0);
  const closing = opening + periodBilled - periodReceived;

  if (!party) return <div className="p-10 text-brand-muted">Loading…</div>;

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-4xl mx-auto" data-testid="party-statement-page">
      <div className="no-print flex items-center justify-between mb-5">
        <BackLink to={`/ledger/party/${id}`} label="Back" testId="party-statement-back" />
        <div className="flex items-center gap-2">
        <Button onClick={async () => {
          try {
            const res = await api.get(`/party/${id}/bills-bundle-pdf`, { params: { date_from: from, date_to: to }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `Bills-Bundle-${party.name.replace(/\s+/g,"_")}-${from}_to_${to}.pdf`; a.click();
            URL.revokeObjectURL(url);
          } catch (e) {
            alert("No bills found in this period for bundle download.");
          }
        }} variant="outline" data-testid="download-party-bundle-pdf-btn" className="rounded-sm border-brand-ink">
          <Layers className="h-4 w-4 mr-1.5" /> Bills Bundle PDF
        </Button>
        <Button onClick={async () => {
          try {
            const res = await api.get(`/party/${id}/statement-pdf`, { params: { date_from: from, date_to: to }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `Statement-${party.name.replace(/\s+/g,"_")}-${from}_to_${to}.pdf`; a.click();
            URL.revokeObjectURL(url);
          } catch (e) {}
        }} variant="outline" data-testid="download-party-statement-pdf-btn" className="rounded-sm border-brand-ink">
          <FileDown className="h-4 w-4 mr-1.5" /> Statement PDF
        </Button>
        <EmailDialog
          endpoint={`/party/${id}/statement-email`}
          queryParams={{ date_from: from, date_to: to }}
          defaultTo={party?.email || ""}
          testIdPrefix="email-statement"
          buttonLabel="Email Statement"
        />
        <EmailDialog
          endpoint={`/party/${id}/bills-bundle-email`}
          queryParams={{ date_from: from, date_to: to }}
          defaultTo={party?.email || ""}
          testIdPrefix="email-bundle"
          buttonLabel="Email Bills Bundle"
        />
        <Button onClick={() => window.print()} data-testid="print-party-statement-btn" className="rounded-sm bg-brand-forest text-white">
          <Printer className="h-4 w-4 mr-1.5" /> Print
        </Button>
        </div>
      </div>

      <Card className="no-print rounded-sm border-brand-line shadow-none p-5 mb-5">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>From</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        </div>
      </Card>

      <div className="print-area bg-white border border-brand-line rounded-sm">
        <div className="px-8 pt-7 pb-5 border-b-2 border-brand-ink">
          <div className="text-center text-[11px] uppercase tracking-[0.3em] text-brand-muted mb-2">Statement of Account</div>
          <div className="flex justify-between items-start gap-6">
            <div className="flex items-start gap-4">
              {company.logo_base64 && <img src={company.logo_base64} alt="" className="h-14 w-14 object-contain border border-brand-line rounded-sm" />}
              <div>
                <div className="font-serif text-2xl text-brand-ink tracking-tight">{company.company_name}</div>
                <div className="text-[11px] text-brand-muted mt-1">{company.address}<br/>{company.city}<br/>GSTIN: {company.gstin} · PAN: {company.pan}</div>
              </div>
            </div>
            <div className="text-xs text-right">
              <div className="uppercase tracking-[0.15em] text-brand-muted text-[10px]">Period</div>
              <div className="font-medium">{formatDate(from)} → {formatDate(to)}</div>
              <div className="mt-2 uppercase tracking-[0.15em] text-brand-muted text-[10px]">Issued On</div>
              <div className="font-medium">{formatDate(todayISO())}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-4 border-b border-brand-line text-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">Party</div>
          <div className="font-serif text-xl text-brand-ink mt-1">{party.name}</div>
          {party.address && <div className="text-xs text-brand-muted">{party.address}</div>}
          <div className="text-xs mt-0.5">
            {party.gstin && <><span className="text-brand-muted">GSTIN:</span> <span className="font-mono">{party.gstin}</span>{"  "}</>}
            {party.pan && <><span className="text-brand-muted">PAN:</span> <span className="font-mono">{party.pan}</span></>}
          </div>
        </div>

        <div className="px-8 py-5">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-brand-ink text-[10px] uppercase tracking-[0.12em]">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Particulars</th>
                <th className="text-left py-2">Reference</th>
                <th className="text-right py-2">Debit</th>
                <th className="text-right py-2">Credit</th>
                <th className="text-right py-2">Balance</th>
              </tr>
              <tr className="border-b border-brand-line bg-secondary/40">
                <td colSpan={5} className="py-2 text-[10px] uppercase tracking-[0.15em] text-brand-muted">Opening Balance (prior to {formatDate(from)})</td>
                <td className="py-2 text-right tabular-nums font-medium">{inr(opening)}</td>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = [
                  ...billsInPeriod.map((b) => ({ date: b.bill_date, kind: `${b.bill_type === "material" ? "Material" : "Transport"} Bill`, ref: b.bill_number, debit: b.total_amount, credit: 0 })),
                  ...paymentsInPeriod.map((p) => ({ date: p.date, kind: "Payment Received", ref: `${p.mode}${p.particulars ? ` · ${p.particulars}` : ""}`, debit: 0, credit: p.amount })),
                ].sort((a, b) => (a.date < b.date ? -1 : 1));
                let bal = opening;
                if (rows.length === 0) return <tr><td colSpan={6} className="py-3 text-center text-brand-muted">No transactions in this period</td></tr>;
                return rows.map((r, i) => {
                  bal += (r.debit || 0) - (r.credit || 0);
                  return (
                    <tr key={i} className="border-b border-brand-line">
                      <td className="py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="py-2">{r.kind}</td>
                      <td className="py-2 font-mono text-[10px] text-brand-muted">{r.ref}</td>
                      <td className="py-2 text-right tabular-nums">{r.debit ? inr(r.debit) : "—"}</td>
                      <td className="py-2 text-right tabular-nums text-brand-forest">{r.credit ? inr(r.credit) : "—"}</td>
                      <td className="py-2 text-right tabular-nums font-medium">{inr(bal)}</td>
                    </tr>
                  );
                });
              })()}
              <tr className="bg-secondary/40 font-semibold border-t-2 border-brand-ink">
                <td colSpan={3} className="py-2 text-[10px] uppercase tracking-[0.15em]">Period Totals</td>
                <td className="py-2 text-right tabular-nums">{inr(periodBilled)}</td>
                <td className="py-2 text-right tabular-nums text-brand-forest">{inr(periodReceived)}</td>
                <td className="py-2"></td>
              </tr>
            </tbody>
          </table>

          {/* Bill-wise outstanding (still pending in this period) */}
          {billsInPeriod.filter((b) => b.balance_due > 0).length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted mb-2">Outstanding bills in this period</div>
              <table className="w-full text-xs">
                <thead className="border-b border-brand-ink">
                  <tr className="text-[10px] uppercase tracking-[0.12em]">
                    <th className="text-left py-1.5">Bill No</th>
                    <th className="text-left py-1.5">Date</th>
                    <th className="text-left py-1.5">Type</th>
                    <th className="text-right py-1.5">Total</th>
                    <th className="text-right py-1.5">Received</th>
                    <th className="text-right py-1.5">Balance</th>
                    <th className="text-center py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billsInPeriod.filter((b) => b.balance_due > 0).map((b) => (
                    <Fragment key={b.id}>
                      <tr className="border-b border-brand-line">
                        <td className="py-1.5 font-mono">
                          <button
                            onClick={() => setExpandedBill(expandedBill === b.id ? null : b.id)}
                            data-testid={`expand-bill-${b.bill_number}`}
                            className="no-print underline-offset-2 hover:underline cursor-pointer">
                            {b.bill_number}
                          </button>
                          <span className="print:inline hidden">{b.bill_number}</span>
                        </td>
                        <td className="py-1.5">{formatDate(b.bill_date)}</td>
                        <td className="py-1.5 capitalize">{b.bill_type}</td>
                        <td className="py-1.5 text-right tabular-nums">{inr(b.total_amount)}</td>
                        <td className="py-1.5 text-right tabular-nums">{inr(b.received_total)}</td>
                        <td className="py-1.5 text-right tabular-nums text-brand-terracotta">{inr(b.balance_due)}</td>
                        <td className="py-1.5 text-center">
                          <Badge variant="outline" className="rounded-sm text-[9px]">{b.status}</Badge>
                        </td>
                      </tr>
                      {expandedBill === b.id && (
                        <tr className="bg-secondary/30 no-print">
                          <td colSpan={7} className="py-2 px-2">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-brand-muted mb-1.5">
                              Bill Line Items · GST {b.gst_percent}% · CGST {inr(b.cgst)} · SGST {inr(b.sgst)}
                              <Link to={`/bills/${b.id}`} className="ml-3 text-brand-forest hover:underline">Open full bill →</Link>
                            </div>
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-brand-muted border-b border-brand-line">
                                  <th className="text-left py-1">Date</th>
                                  <th className="text-left py-1">Vehicle</th>
                                  <th className="text-left py-1">Description</th>
                                  <th className="text-right py-1">Wgt</th>
                                  <th className="text-right py-1">Rate</th>
                                  <th className="text-right py-1">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(b.lines || []).map((ln, li) => (
                                  <tr key={li} className="border-b border-brand-line/50">
                                    <td className="py-1">{formatDate(ln.entry_date)}</td>
                                    <td className="py-1 font-mono">{ln.vehicle_number}</td>
                                    <td className="py-1 text-brand-muted">{ln.description || "—"}</td>
                                    <td className="py-1 text-right tabular-nums">{ln.weight}</td>
                                    <td className="py-1 text-right tabular-nums">{inr(ln.rate)}</td>
                                    <td className="py-1 text-right tabular-nums font-medium">{inr(ln.amount)}</td>
                                  </tr>
                                ))}
                                <tr className="font-semibold">
                                  <td colSpan={5} className="py-1 text-right">Subtotal</td>
                                  <td className="py-1 text-right tabular-nums">{inr(b.subtotal)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 border-t-2 border-brand-ink pt-4 grid grid-cols-2 gap-6">
            <div className="text-[11px] text-brand-muted">
              <div className="uppercase tracking-[0.15em] text-[10px] mb-1">Amount in words</div>
              <div className="font-medium text-brand-ink uppercase">{amountInWords(Math.abs(closing))}{closing < 0 ? " (CR.)" : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Closing Balance Due</div>
              <div className={`font-serif text-3xl tabular-nums ${closing > 0 ? "text-brand-terracotta" : "text-brand-forest"}`}>{inr(closing)}</div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-6 text-[11px]">
            <div>
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Acknowledged by Party</div>
              <div className="h-16"></div>
              <div className="border-t border-brand-ink pt-1.5 inline-block min-w-[200px]">Signature & Date</div>
            </div>
            <div className="text-right">
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">For {company.company_name}</div>
              <div className="h-16"></div>
              <div className="border-t border-brand-ink pt-1.5 inline-block min-w-[200px]">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
